import { useEffect, useState } from "react";

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden bg-[#060e1c]">
      <style>{`
        @keyframes heroZoom {
          0%   { transform: scale(1.0); }
          100% { transform: scale(1.08); }
        }
        .hero-zoom { animation: heroZoom 22s ease-in-out infinite alternate; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fu1 { animation: fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.12s both; }
        .fu2 { animation: fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.28s both; }
        .fu3 { animation: fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.44s both; }
        .fu4 { animation: fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.60s both; }
        .fu5 { animation: fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.78s both; }
        @keyframes pulseRing {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50%       { transform: scale(1.25); opacity: 0.2; }
        }
        .pulse-ring { animation: pulseRing 2.4s ease-in-out infinite; }
      `}</style>

      {/* Background Image + Layers */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/a3a6f9f618ed780cef2ce32951b9d0eb.jpg"
          alt="Premium HVAC service Dallas residential"
          className="hero-zoom absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
        />
        {/* Left-heavy dark overlay for text */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#060e1c]/98 via-[#060e1c]/88 to-[#060e1c]/20" />
        {/* Top-to-bottom depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#060e1c]/50 via-transparent to-[#060e1c]/65" />
        {/* Bottom bleed */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#060e1c] to-transparent" />
        {/* Edge vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_80%_at_70%_50%,transparent_40%,rgba(6,14,28,0.55)_100%)]" />
      </div>

      {/* Decorative top line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0EA5E9]/60 via-[#38bdf8]/20 to-transparent" />

      {/* Content */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-14 pt-36 pb-28 transition-opacity duration-700 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="max-w-[700px]">

          {/* Location pill */}
          <div className="fu1 inline-flex items-center gap-3 mb-9">
            <div className="relative flex items-center justify-center w-5 h-5 flex-shrink-0">
              <span className="pulse-ring absolute w-4 h-4 rounded-full bg-[#0EA5E9]/40 flex-shrink-0" />
              <span className="relative w-2 h-2 rounded-full bg-[#38bdf8] flex-shrink-0" />
            </div>
            <span className="font-['Inter'] text-white/75 text-[11.5px] font-[500] tracking-[0.20em] uppercase whitespace-nowrap">
              McKinney &amp; Dallas Metro &nbsp;·&nbsp; Licensed HVAC
            </span>
          </div>

          {/* H1 — Large editorial headline */}
          <h1 className="fu2 mb-7 leading-[1.04]">
            <span className="block font-['Playfair_Display'] text-white font-[800] text-[2.8rem] md:text-[3.6rem] lg:text-[4.2rem] xl:text-[4.85rem] tracking-[-0.01em] drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
              Precision Cooling
            </span>
            <span className="block font-['Playfair_Display'] text-white font-[800] text-[2.8rem] md:text-[3.6rem] lg:text-[4.2rem] xl:text-[4.85rem] tracking-[-0.01em] drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
              Built for&nbsp;
              <span className="relative inline-block">
                <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#38bdf8] to-[#7dd3fc]">
                  Texas Heat.
                </em>
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-[#38bdf8]/60 to-transparent rounded-full" />
              </span>
            </span>
          </h1>

          {/* Divider line */}
          <div className="fu2 w-12 h-[2px] bg-gradient-to-r from-[#0EA5E9] to-[#0EA5E9]/20 mb-7 rounded-full" />

          {/* Subheadline */}
          <p className="fu3 font-['Inter'] text-white text-[1.08rem] md:text-[1.18rem] leading-[1.82] mb-11 max-w-[530px] font-[400]">
            Fast diagnostics, precision repairs, and performance-driven installs — so your home stays comfortable when the heat climbs past 100°.
          </p>

          {/* CTA Buttons */}
          <div className="fu4 flex flex-col sm:flex-row gap-3.5 mb-12">
            <a
              href="#quote"
              className="inline-flex items-center justify-center gap-2.5 bg-white text-[#0a1f3c] hover:bg-gray-50 font-['Inter'] font-[700] text-[0.95rem] px-9 py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_8px_32px_rgba(255,255,255,0.10)] hover:shadow-[0_12px_40px_rgba(255,255,255,0.16)] hover:-translate-y-[2px] tracking-[0.005em]"
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-[#1e3a5f] text-base"></i>
              </div>
              Request Service
            </a>
            <a
              href="tel:2148703185"
              className="inline-flex items-center justify-center gap-2.5 border border-white/22 hover:border-white/45 bg-white/5 hover:bg-white/10 text-white font-['Inter'] font-[600] text-[0.95rem] px-9 py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm tracking-[0.005em]"
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-fill text-[#7dd3fc] text-sm"></i>
              </div>
              Call 214-870-3185
            </a>
          </div>

          {/* Trust Row */}
          <div className="fu5 pt-8 border-t border-white/20">
            <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
              {[
                { icon: "ri-shield-check-fill", label: "Licensed & Insured" },
                { icon: "ri-timer-flash-line", label: "Same-Day Response" },
                { icon: "ri-map-pin-2-line", label: "Serving McKinney & Dallas" },
                { icon: "ri-star-fill", label: "5-Star Rated" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-[18px] h-[18px] flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#38bdf8] text-[13px]`}></i>
                  </div>
                  <span className="font-['Inter'] text-white text-[12.5px] font-[500] whitespace-nowrap">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-6 lg:left-14 z-10 flex flex-col items-start gap-2">
        <span className="font-['Inter'] text-white/45 text-[9.5px] tracking-[0.22em] uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/45 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
